/**
 * PROTOTYPE variant C: status strip and timeline.
 *
 * The hierarchy is time. An identity line names the machine, a strip of
 * status pills answers "is anything wrong" at a glance, and a vertical
 * timeline tells what CMMD saw and when, newest first. Every field is still
 * reachable, folded under "All fields" so the page stays one screen tall.
 */
import { cn } from "../../../lib/utils";
import { StatusDot, StatusPill, formatTimestamp } from "../DiagnosticsPanelPrimitives";
import { DiagnosticsTargetPicker } from "../DiagnosticsTargetPicker";
import { PrototypeRefreshButton } from "./PrototypeRefreshButton";
import type { DiagnosticsPrototypeVariantProps } from "./diagnosticsPrototypeProps";

const STRIP_LABELS = new Set([
  "Lifecycle",
  "Service",
  "Forge build",
  "Credentials",
  "Codex delivery",
  "Registry",
  "Tools",
]);
const IDENTITY_LABELS = new Set(["Runtime", "Machine", "Device", "Platform"]);

export function DiagnosticsPrototypeVariantC({
  model,
  targets,
  refresh,
}: DiagnosticsPrototypeVariantProps) {
  const facts = model.groups.flatMap((group) => group.facts);
  const strip = facts.filter((fact) => STRIP_LABELS.has(fact.label));
  const identity = facts.filter((fact) => IDENTITY_LABELS.has(fact.label));
  const moments = [
    ...model.timeline,
    ...model.events.map((event) => ({
      at: event.at,
      title: event.title,
      detail: event.detail,
      tone: event.tone,
    })),
  ].toSorted((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));

  return (
    <div className="space-y-8 px-3 sm:px-4">
      <div className="flex flex-wrap items-center gap-2">
        <DiagnosticsTargetPicker
          groups={targets.groups}
          active={targets.active}
          onSelect={targets.onSelect}
        />
        <PrototypeRefreshButton refresh={refresh} />
      </div>

      <div>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h3 className="text-lg font-semibold tracking-[-0.025em] text-foreground">
            {model.target?.label ?? "Select a target"}
          </h3>
          {identity.map((fact) => (
            <span key={fact.label} className="font-mono text-xs text-muted-foreground">
              {fact.label.toLowerCase()} {fact.value}
            </span>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <StatusPill label={model.verdict.label} tone={model.verdict.tone} />
          {strip.map((fact) => (
            <StatusPill
              key={fact.label}
              label={`${fact.label}: ${fact.value}`}
              tone={fact.tone ?? "muted"}
            />
          ))}
        </div>
        {model.verdict.disagreement ? (
          <p className="mt-3 text-sm text-warning-foreground">{model.verdict.disagreement}</p>
        ) : null}
      </div>

      <ol className="relative ml-2 border-l border-border/70 pl-6">
        {moments.length === 0 ? (
          <li className="text-sm text-muted-foreground">Nothing observed yet.</li>
        ) : (
          moments.map((moment) => (
            <li key={`${moment.title}-${moment.at}`} className="relative pb-6 last:pb-0">
              <span className="absolute top-1.5 -left-[1.9rem] flex size-4 items-center justify-center rounded-full bg-background">
                <StatusDot tone={moment.tone} />
              </span>
              <p className="text-xs text-muted-foreground">{formatTimestamp(moment.at)}</p>
              <p className="text-sm font-medium text-foreground">{moment.title}</p>
              <p className="text-sm text-muted-foreground">{moment.detail}</p>
            </li>
          ))
        )}
      </ol>

      <details className="group">
        <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
          All fields ({facts.length})
        </summary>
        <div className="mt-4 grid gap-x-10 gap-y-6 sm:grid-cols-2">
          {model.groups.map((group) => (
            <section key={group.id}>
              <p className="text-xs text-muted-foreground">{group.source}</p>
              <dl className="mt-2 space-y-1.5">
                {group.facts.map((fact) => (
                  <div
                    key={fact.label}
                    className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 text-sm"
                  >
                    <dt className="text-muted-foreground">{fact.label}</dt>
                    <dd
                      className={cn(
                        "truncate text-foreground",
                        fact.mono ? "font-mono text-xs" : null,
                      )}
                    >
                      {fact.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>
      </details>
    </div>
  );
}
